import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomInput from "../OnboardingComponents/CustomInput"
import CategoriesMultiselect from "../OnboardingComponents/CategoriesMultiselect"
import PreferredCitySelect from "../Common/PreferredCitySelect"
import { ICONS } from "../../constants/icons"
import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const ADDRESS_TYPE_META = [
  { value: "primary", label: "Primary City" },
  { value: "secondary", label: "Secondary City" },
  { value: "tertiary", label: "Tertiary City" },
]

const REASON_MIN = 10

const emptyDestinations = () => ({
  primary: "",
  secondary: "",
  tertiary: "",
})

const formatCityOptionLabel = city => {
  if (!city) return "N/A"
  if (typeof city === "string") return city.trim() || "N/A"
  const name = city.city || city.name || ""
  const state = city.state || ""
  if (name && state) return `${name}, ${state}`
  return name || "N/A"
}

const getAddressLabel = address => {
  if (!address) return "N/A"
  return formatCityOptionLabel(address.city || address.city_name)
}

const getAddressTypeLabel = type =>
  ADDRESS_TYPE_META.find(option => option.value === type)?.label || "City"

const getCityLabelById = (cityId, labelMap = {}) => {
  if (!cityId) return ""
  return labelMap[String(cityId)] || ""
}

const getVisibleFieldError = (submitCount, touched, error) => {
  if (!error) return undefined
  if (submitCount > 0 || touched) return error
  return undefined
}

const getDestinationFieldError = (submitCount, touched, errors, type) => {
  if (submitCount === 0 && !touched?.destinations?.[type]) return null
  if (typeof errors.destinations === "string") return errors.destinations
  return errors.destinations?.[type] || null
}

/**
 * RequestChangeCityModal
 * ------------------------------------------------------------------
 * Request city changes for one or more addresses in a single call.
 * POST /api/v1/city-update-requests
 * { requests: [{ address_id, new_city_id, reason }, ...] }
 */
const RequestChangeCityModal = ({
  isOpen,
  onClose,
  addresses = [],
  onSubmit,
  onSuccess,
}) => {
  const [destinationLabels, setDestinationLabels] = useState({})

  const addressesByType = useMemo(() => {
    const map = {}
    ;(addresses || []).forEach(address => {
      const type = String(address?.address_type || "")
        .trim()
        .toLowerCase()
      if (type && !map[type]) {
        map[type] = address
      }
    })
    return map
  }, [addresses])

  const addressTypeOptions = useMemo(
    () =>
      ADDRESS_TYPE_META.filter(option =>
        Boolean(addressesByType[option.value]?.id),
      ),
    [addressesByType],
  )

  const defaultAddressTypes = useMemo(() => {
    if (addressesByType.primary?.id) return ["primary"]
    return addressTypeOptions[0]?.value ? [addressTypeOptions[0].value] : []
  }, [addressesByType, addressTypeOptions])

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        addressTypes: yup
          .array()
          .of(yup.string())
          .min(1, "Select at least one city address to update"),
        destinations: yup
          .object()
          .test(
            "destinations-required",
            "Select a destination city for each selected address",
            function validateDestinations(destinations) {
              const types = this.parent.addressTypes || []
              const missing = types.filter(
                type => !String(destinations?.[type] || "").trim(),
              )
              if (missing.length === 0) return true
              return this.createError({
                path: `destinations.${missing[0]}`,
                message: `Select a new city for ${getAddressTypeLabel(
                  missing[0],
                )}`,
              })
            },
          ),
        reason: yup
          .string()
          .trim()
          .required("Reason for request is required")
          .min(
            REASON_MIN,
            `Please provide at least ${REASON_MIN} characters`,
          ),
      }),
    [],
  )

  useEffect(() => {
    if (!isOpen) setDestinationLabels({})
  }, [isOpen])

  const buildRequestsPayload = values => {
    const reason = String(values.reason || "").trim()
    const types = Array.isArray(values.addressTypes) ? values.addressTypes : []

    return types
      .map(type => {
        const address = addressesByType[type]
        const newCityId = String(values.destinations?.[type] || "").trim()
        if (!address?.id || !newCityId) return null
        return {
          address_id: address.id,
          new_city_id: newCityId,
          reason,
        }
      })
      .filter(Boolean)
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const requests = buildRequestsPayload(values)

    if (requests.length === 0) {
      toast.error(
        "Select at least one address and destination city.",
        TOAST_OPTIONS,
      )
      setSubmitting(false)
      return
    }

    const payload = { requests }

    try {
      if (onSubmit) {
        await onSubmit(payload)
      } else {
        const response = await post(endpoints.createCityUpdateRequest, payload)

        if (!response?.data?.success) {
          toastApiError(response, "Failed to submit city change request")
          return
        }

        toast.success(
          response?.data?.message ||
            (requests.length > 1
              ? "City change requests submitted"
              : "City change request submitted"),
          TOAST_OPTIONS,
        )
      }

      if (onSuccess) {
        await onSuccess(payload)
      }

      resetForm()
      onClose()
    } catch (error) {
      toastApiError(error, "Failed to submit city change request")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--city-request"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body city-request-modal__body">
        <Formik
          key={
            isOpen
              ? `city-request-${defaultAddressTypes.join("-") || "empty"}`
              : "city-request-closed"
          }
          initialValues={{
            addressTypes: defaultAddressTypes,
            destinations: emptyDestinations(),
            reason: "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            submitCount,
          }) => {
            const selectedTypes = Array.isArray(values.addressTypes)
              ? values.addressTypes
              : []
            const destinationsError =
              submitCount > 0 &&
              typeof errors.destinations === "string"
                ? errors.destinations
                : null
            const readyCount = selectedTypes.filter(type =>
              Boolean(String(values.destinations?.[type] || "").trim()),
            ).length
            const reasonLength = String(values.reason || "").trim().length
            const reasonReady = reasonLength >= REASON_MIN
            const destinationsReady =
              selectedTypes.length > 0 && readyCount === selectedTypes.length
            const stepItems = [
              {
                id: "addresses",
                label: "Addresses",
                complete: selectedTypes.length > 0,
              },
              {
                id: "destinations",
                label: "New cities",
                complete: destinationsReady,
                hidden: selectedTypes.length === 0,
              },
              {
                id: "reason",
                label: "Reason",
                complete: reasonReady,
              },
            ].filter(step => !step.hidden)

            return (
              <Form className="city-request-modal" noValidate>
                <div className="city-request-modal__main">
                  <header className="city-request-modal__header">
                    <h3 className="city-request-modal__title">
                      Request City Change
                    </h3>
                    <p className="city-request-modal__subtitle">
                      Choose one or more addresses to update and pick a
                      destination city for each.
                    </p>

                    <ol className="city-request-modal__steps">
                      {stepItems.map(step => (
                        <li
                          key={step.id}
                          className={`city-request-modal__step${
                            step.complete
                              ? " city-request-modal__step--complete"
                              : ""
                          }`}
                        >
                          {step.complete ? (
                            <span className="city-request-modal__step-index">
                              <img
                                src={ICONS.check}
                                alt=""
                                width={12}
                                height={12}
                              />
                            </span>
                          ) : null}
                          <span className="city-request-modal__step-label">
                            {step.label}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </header>

                  <div className="city-request-modal__fields">
                    <section className="city-request-modal__section">
                      <h4 className="city-request-modal__section-title">
                        Addresses to update
                      </h4>

                      <CategoriesMultiselect
                        className="city-request-modal__address-types"
                        label="Which city addresses do you want to change?"
                        helperText="Select all that apply"
                        name="addressTypes"
                        required
                        options={addressTypeOptions}
                        selected={selectedTypes}
                        checkIconSrc={ICONS.check}
                        onChange={next => {
                          const nextTypes = Array.isArray(next) ? next : []
                          const nextDestinations = {
                            ...emptyDestinations(),
                            ...Object.fromEntries(
                              nextTypes.map(type => [
                                type,
                                values.destinations?.[type] || "",
                              ]),
                            ),
                          }

                          setFieldValue("addressTypes", nextTypes)
                          setFieldValue("destinations", nextDestinations)
                        }}
                        error={getVisibleFieldError(
                          submitCount,
                          touched.addressTypes,
                          errors.addressTypes,
                        )}
                      />

                      {addressTypeOptions.length === 0 ? (
                        <p className="city-request-modal__hint city-request-modal__hint--error">
                          No city addresses found. Add them in your profile
                          first.
                        </p>
                      ) : null}
                    </section>

                    {selectedTypes.length > 0 ? (
                      <section className="city-request-modal__section">
                        <div className="city-request-modal__section-head">
                          <h4 className="city-request-modal__section-title">
                            New destination cities
                          </h4>
                          <span
                            className={`city-request-modal__section-meta${
                              destinationsReady
                                ? " city-request-modal__section-meta--complete"
                                : ""
                            }`}
                          >
                            {readyCount}/{selectedTypes.length} selected
                          </span>
                        </div>

                        <div
                          className="city-request-modal__progress"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={selectedTypes.length}
                          aria-valuenow={readyCount}
                          aria-label="Destination cities selected"
                        >
                          <span
                            className="city-request-modal__progress-bar"
                            style={{
                              width: `${
                                selectedTypes.length
                                  ? (readyCount / selectedTypes.length) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>

                        {destinationsError ? (
                          <p
                            className="city-request-modal__panel-error"
                            role="alert"
                          >
                            {destinationsError}
                          </p>
                        ) : null}

                        <div className="city-request-modal__destinations">
                          {selectedTypes.map(type => {
                            const address = addressesByType[type]
                            const currentLabel = getAddressLabel(address)
                            const selectedDestinationId =
                              values.destinations?.[type] || ""
                            const selectedDestinationLabel = getCityLabelById(
                              selectedDestinationId,
                              destinationLabels,
                            )
                            const destinationError = getDestinationFieldError(
                              submitCount,
                              touched,
                              errors,
                              type,
                            )
                            const isDestinationComplete = Boolean(
                              selectedDestinationId,
                            )

                            return (
                              <article
                                key={type}
                                className={`city-request-modal__destination-card${
                                  destinationError
                                    ? " city-request-modal__destination-card--error"
                                    : isDestinationComplete
                                      ? " city-request-modal__destination-card--complete"
                                      : ""
                                }`}
                              >
                                <div className="city-request-modal__destination-head">
                                  <span className="city-request-modal__destination-badge">
                                    {getAddressTypeLabel(type)}
                                  </span>
                                </div>

                                <div className="city-request-modal__city-flow">
                                  <div className="city-request-modal__city-block">
                                    <span className="city-request-modal__city-label">
                                      Current
                                    </span>
                                    <p className="city-request-modal__city-value">
                                      {currentLabel}
                                    </p>
                                  </div>

                                  <span
                                    className="city-request-modal__city-arrow"
                                    aria-hidden="true"
                                  >
                                    <img
                                      src={ICONS.arrowRightBlack}
                                      alt=""
                                      width={16}
                                      height={16}
                                    />
                                  </span>

                                  <div className="city-request-modal__city-block city-request-modal__city-block--new">
                                    <PreferredCitySelect
                                      label="New city"
                                      loadFromApi
                                      placeholder="Select destination"
                                      value={selectedDestinationId}
                                      valueLabel={selectedDestinationLabel}
                                      onChange={(value, option) => {
                                        setFieldValue(
                                          `destinations.${type}`,
                                          value,
                                        )
                                        setDestinationLabels(prev => {
                                          const next = { ...prev }
                                          if (value && option?.label) {
                                            next[String(value)] = option.label
                                          } else if (!value) {
                                            delete next[
                                              String(selectedDestinationId)
                                            ]
                                          }
                                          return next
                                        })
                                      }}
                                      error={destinationError}
                                    />
                                  </div>
                                </div>

                                {selectedDestinationLabel ? (
                                  <p className="city-request-modal__destination-summary">
                                    Requesting change from{" "}
                                    <strong>{currentLabel}</strong> to{" "}
                                    <strong>{selectedDestinationLabel}</strong>
                                  </p>
                                ) : null}
                              </article>
                            )
                          })}
                        </div>
                      </section>
                    ) : (
                      <p className="city-request-modal__empty-selection">
                        Select at least one address above to choose new cities.
                      </p>
                    )}

                    <section
                      className={`city-request-modal__section city-request-modal__section--reason${
                        reasonReady
                          ? " city-request-modal__section--reason-ready"
                          : ""
                      }`}
                    >
                      <h4 className="city-request-modal__section-title">
                        Reason for request
                      </h4>

                      <CustomInput
                        label="Tell us why you want to change"
                        name="reason"
                        textarea
                        rows={4}
                        required
                        placeholder="Briefly explain why you are requesting this city change..."
                        value={values.reason}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("reason", true)}
                        error={getVisibleFieldError(
                          submitCount,
                          touched.reason,
                          errors.reason,
                        )}
                        helperText={`${reasonLength}/${REASON_MIN}+ characters`}
                      />
                    </section>
                  </div>
                </div>

                <div className="city-request-modal__actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="city-request-modal__cancel"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="city-request-modal__submit"
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      addressTypeOptions.length === 0 ||
                      selectedTypes.length === 0
                    }
                  >
                    Submit Request
                    {selectedTypes.length > 1
                      ? ` (${selectedTypes.length})`
                      : ""}
                  </CustomButton>
                </div>
              </Form>
            )
          }}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

RequestChangeCityModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
}

export default RequestChangeCityModal
